import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import FarmerInvoiceService from '@/services/public/FarmerInvoiceService.js'
import i18n from '@/i18n/i18n.js'
import { errorMessage } from '@/utilities/helpers.js'
import { ORDER_ERROR_MESSAGES } from '@/constants/errorMessages.js'

const { t } = i18n.global

const HTTP_UNAUTHORIZED = 401

export const useFarmerInvoiceStore = defineStore('publicFarmerInvoice', () => {
  const notificationStore = useNotificationStore()

  const order = ref(null)
  const invoice_pdf = ref(null)
  const late_fee_invoice_pdf = ref(null)
  const receipt_pdf = ref(null)
  const paid_promissory_note = ref(null)

  const getOrder = params => {
    return new Promise((resolve, reject) => {
      FarmerInvoiceService.getOrder(params.orderId, params.tckn)
        .then(response => {
          order.value = response.data
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getInvoiceError + errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const getInvoice = params => {
    return new Promise((resolve, reject) => {
      FarmerInvoiceService.getInvoice(params.orderId, params.tckn)
        .then(response => {
          invoice_pdf.value = response
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.public.invoice.invoice')
            })
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const getLateFeeInvoice = params => {
    return new Promise((resolve, reject) => {
      FarmerInvoiceService.getLateFeeInvoice(params.orderId, params.tckn)
        .then(response => {
          late_fee_invoice_pdf.value = response
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.public.invoice.late_fee_invoice')
            })
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const getOrderReceipt = params => {
    return new Promise((resolve, reject) => {
      FarmerInvoiceService.getOrderReceipt(params.orderId, params.tckn)
        .then(response => {
          receipt_pdf.value = response
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.public.invoice.order_receipt')
            })
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const getPaidPromissoryNote = params => {
    return new Promise((resolve, reject) => {
      FarmerInvoiceService.getPaidPromissoryNote(params.orderId, params.tckn)
        .then(response => {
          paid_promissory_note.value = response
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.public.invoice.order_receipt')
            })
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const resetState = () => {
    order.value = null
    invoice_pdf.value = null
    late_fee_invoice_pdf.value = null
    receipt_pdf.value = null
  }

  const getOrderByHash = async params => {
    try {
      const response = await FarmerInvoiceService.getOrderByHash(params.hash, params.tckn)
      order.value = response.data
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message:
          err.response?.status === HTTP_UNAUTHORIZED
            ? t('views.pages.unauthorized_access')
            : ORDER_ERROR_MESSAGES.getInvoiceError + errorMessage(err)
      })
      throw err
    }
  }

  const getInvoiceByHash = async params => {
    try {
      const response = await FarmerInvoiceService.getInvoiceByHash(params.hash, params.tckn)
      invoice_pdf.value = response
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: t('store.shared.fetching_problem', { element: t('store.public.invoice.invoice') })
      })
      throw err
    }
  }

  const getOrderReceiptByHash = async params => {
    try {
      const response = await FarmerInvoiceService.getOrderReceiptByHash(params.hash, params.tckn)
      receipt_pdf.value = response
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: t('store.shared.fetching_problem', { element: t('store.public.invoice.order_receipt') })
      })
      throw err
    }
  }

  const getLateFeeInvoiceByHash = async params => {
    try {
      const response = await FarmerInvoiceService.getLateFeeInvoiceByHash(params.hash, params.tckn)
      late_fee_invoice_pdf.value = response
      return response
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message: t('store.shared.fetching_problem', { element: t('store.public.invoice.late_fee_invoice') })
      })
      throw err
    }
  }

  return {
    resetState,
    getOrderByHash,
    getInvoiceByHash,
    getOrderReceiptByHash,
    getLateFeeInvoiceByHash,
    order,
    invoice_pdf,
    late_fee_invoice_pdf,
    receipt_pdf,
    paid_promissory_note,
    getOrder,
    getInvoice,
    getLateFeeInvoice,
    getOrderReceipt,
    getPaidPromissoryNote
  }
})
